import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiPublicPost } from '../api';
import { resetErrorMessage } from './resetErrors';

/**
 * Password reset by emailed code, run by us rather than by Cognito.
 *
 * Cognito's own forgot-password requires a verified address (accountRecovery is
 * EMAIL_ONLY) and signup no longer verifies one (#697), so the stock flow fails for
 * exactly the accounts most likely to need it — someone who signed up on their phone
 * ten minutes ago and mistyped their password.
 *
 * This works the same either way, and completing it marks the address verified,
 * since receiving the code is the proof Cognito wanted in the first place. (#698)
 */
const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiPublicPost('/recover', { action: 'emailCode', email });
      const data = (await res.body.json()) as { message?: string };
      // The API answers identically whether or not an account exists, so the step
      // always advances — anything else would turn this into an account oracle.
      setNotice(data?.message || 'If an account exists for that address, a reset code is on its way.');
      setStep('confirm');
    } catch {
      // The request answers the same for every address, so a failure has nothing to say
      // but this.
      setError('Could not send a reset code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await apiPublicPost('/recover', { action: 'emailReset', email, code, newPassword });
      navigate('/login?recovered=true', { replace: true });
    } catch (err) {
      setError(await resetErrorMessage(err, 'That code is not valid or has expired. Please request a new one.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page-container container">
      <div className="auth-header-section">
        <img src="/chaptery-mark.svg" alt="Chaptery Logo" className="auth-logo" />
        <h1>Reset Your Password</h1>
        <p className="subtitle">
          {step === 'request'
            ? 'We will email you a code to set a new password.'
            : 'Enter the code we emailed you, and choose a new password.'}
        </p>
      </div>

      <div className="auth-wrapper">
        <form className="recover-form" onSubmit={step === 'request' ? requestCode : submitReset}>
          {error && (
            <div className="feedback-banner feedback-error" role="alert" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}
          {notice && step === 'confirm' && (
            <div className="feedback-banner feedback-success" style={{ marginBottom: '1rem' }}>
              {notice}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="reset-email">Email Address</label>
            <input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              disabled={step === 'confirm'}
            />
          </div>

          {step === 'confirm' && (
            <>
              <div className="form-group">
                <label htmlFor="reset-code">Reset Code</label>
                <input
                  id="reset-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6-digit code"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="reset-password">New Password</label>
                <p className="form-hint">At least 8 characters, with an uppercase letter, a lowercase letter and a number.</p>
                <input
                  id="reset-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter a new password"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="reset-confirm">Confirm New Password</label>
                <input
                  id="reset-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                />
              </div>
            </>
          )}

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting
              ? (step === 'request' ? 'Sending…' : 'Resetting…')
              : (step === 'request' ? 'Send Reset Code' : 'Reset Password')}
          </button>

          {step === 'confirm' && (
            <button
              type="button"
              className="auth-link-btn"
              style={{ marginTop: '0.75rem' }}
              // Clear the code and passwords too. Leaving them meant going back,
              // entering a different address, and finding the confirm step
              // pre-filled with the first address's code and intended password —
              // easy to submit without noticing.
              onClick={() => {
                setStep('request');
                setNotice(null);
                setError(null);
                setCode('');
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              Use a different email address
            </button>
          )}
        </form>
      </div>

      <div className="auth-bottom-links">
        <Link to="/recover">Can't access your email? Use your recovery key</Link>
        <span className="separator">|</span>
        <Link to="/login">Back to Sign In</Link>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
